export type CamelToSnakeCase<S extends string> = S extends ''
	? S
	: S extends `${infer F}${infer R}`
	? F extends '_'
		? `_${CamelToSnakeCase<R>}`
		: R extends ''
		? `${Lowercase<F>}`
		: R extends `_${string}`
		? `${Lowercase<F>}${CamelToSnakeCase<R>}`
		: R extends Capitalize<R>
		? `${Lowercase<F>}_${CamelToSnakeCase<R>}`
		: `${Lowercase<F>}${CamelToSnakeCase<R>}`
	: S

export type KeysToSnakeCase<T> = T extends readonly [any, ...any[]]
	? { [K in keyof T]: KeysToSnakeCase<T[K]> }
	: T extends Array<infer U>
	? Array<KeysToSnakeCase<U>>
	: T extends Record<string | number | symbol, any>
	? { [K in keyof T as CamelToSnakeCase<string & K>]: KeysToSnakeCase<T[K]> }
	: T

export const toSnakeCase = <T extends string>(str: T): CamelToSnakeCase<T> =>
	str
		.replace(/([A-Z])/g, '_$1')
		.replace(/^_/, '')
		.toLowerCase() as CamelToSnakeCase<T>

/**
 * Transforms a nested object or array that has camelCase fields to snake_cased
 * @param obj Nested object or array that has camelCase fields
 * @returns The same object with keys transformed to snake_cased
 */
export const objectToSnakeCased = <T>(obj: T): KeysToSnakeCase<T> => {
	// Handle arrays
	if (Array.isArray(obj)) {
		return obj.map((v) =>
			v && typeof v === 'object' ? objectToSnakeCased(v) : v,
		) as unknown as KeysToSnakeCase<T>
	}

	// Handle plain objects
	if (obj && typeof obj === 'object') {
		const entries = Object.entries(obj as Record<string, any>).map(
			([key, value]) => {
				const newKey = toSnakeCase(key as string)
				let newValue: any = value
				if (Array.isArray(value)) {
					newValue = value.map((v) =>
						v && typeof v === 'object' ? objectToSnakeCased(v) : v,
					)
				} else if (value && typeof value === 'object') {
					newValue = objectToSnakeCased(value)
				}
				return [newKey, newValue]
			},
		)
		return Object.fromEntries(entries) as KeysToSnakeCase<T>
	}

	// Primitives
	return obj as unknown as KeysToSnakeCase<T>
}

export type SnakeToCamelCase<S extends string> =
	S extends `${infer Head}_${infer Tail}`
		? `${Head}${Tail extends `${infer F}${infer R}`
				? `${Uppercase<F>}${SnakeToCamelCase<R>}`
				: ''}`
		: S

export type KeysToCamelCase<T> = T extends readonly [any, ...any[]]
	? { [K in keyof T]: KeysToCamelCase<T[K]> }
	: T extends Array<infer U>
	? Array<KeysToCamelCase<U>>
	: T extends Record<string | number | symbol, any>
	? { [K in keyof T as SnakeToCamelCase<string & K>]: KeysToCamelCase<T[K]> }
	: T

export const toCamelCase = <T extends string>(str: T): SnakeToCamelCase<T> =>
	str.replace(/_([a-z])/g, (_, c) => c.toUpperCase()) as SnakeToCamelCase<T>

/**
 * Transforms a nested object or array that has snake_cased fields to camelCased
 * @param obj Nested object or array that has snake_cased fields
 * @returns The same object with keys transformed to camelCased
 */
export const objectToCamelCased = <T>(obj: T): KeysToCamelCase<T> => {
	// Handle arrays
	if (Array.isArray(obj)) {
		return obj.map((v) =>
			v && typeof v === 'object' ? objectToCamelCased(v) : v,
		) as unknown as KeysToCamelCase<T>
	}

	// Handle plain objects
	if (obj && typeof obj === 'object') {
		const entries = Object.entries(obj as Record<string, any>).map(
			([key, value]) => {
				const newKey = toCamelCase(key as string)
				let newValue: any = value
				if (Array.isArray(value)) {
					newValue = value.map((v) =>
						v && typeof v === 'object' ? objectToCamelCased(v) : v,
					)
				} else if (value && typeof value === 'object') {
					newValue = objectToCamelCased(value)
				}
				return [newKey, newValue]
			},
		)
		return Object.fromEntries(entries) as KeysToCamelCase<T>
	}

	// Primitives
	return obj as unknown as KeysToCamelCase<T>
}
